import { ENDPOINTS } from 'other/config';
import { http } from 'services/http';
import { MultiSelect } from 'components/common/MultiSelect/MultiSelect';
import { stringComparator } from 'other/helpers';
import { VESSEL_TYPE_LIST } from 'pages/Map2/controls/MapControlPane/VesselMenu/vesselTypes';

import {
  TCatchesPeriod,
  TCatchFilterSettings,
  THistoricalCatchCluster,
  TSpecieSimple,
  TVesselSimple,
  TVesselTripResponse
} from '../../../types/catches';
import {
  THistoricalCatchPoint,
  THistoricalCatchRecord
} from '../../../types/catches';
import { THttpResponse } from 'services/HttpClass';
import { TIdLocationDict, TSelectOption } from 'types';

/**/
const DEFAULT_COLOR = '#E0E0E0';
const COLOR_LIST = ['#2297A0', '#94BFFF', '#3DDD9A', '#C39EFF', '#FFBE72'];

/**/
const ZERO = 1;
const WEEKS_IN_YEAR = 53;

/**/
let historyController;
let pointsController;
let optionsController;

const PARAMS_MAP = {
  end: 'end',
  equipment: 'gearIds',
  species: 'specieIds',
  start: 'start',
  types: 'vesselTypeIds',
  vessels: 'vesselIds',
  weights: 'weightRangeIds',
  years: 'years'
};

/**
 *
 */
export function getColourByYear(year: number) {
  const thisYear = new Date().getFullYear();
  return COLOR_LIST[thisYear - year] || DEFAULT_COLOR;
}

/**
 *
 */
export class CatchHistoryRecord {
  week: number;

  constructor(week: number, years: number[]) {
    this.week = week;
    years.forEach((y) => this.addValue(y));
  }

  addValue(year: number, value: number = ZERO) {
    this[String(year)] = value;
  }
}

/**
 *
 */
function mapCatchData(data, years: number[]): CatchHistoryRecord[] {
  const map = new Map();

  Array(WEEKS_IN_YEAR)
    .fill(null)
    .forEach((_, idx) =>
      map.set(idx + 1, new CatchHistoryRecord(idx + 1, years))
    );

  data?.forEach(({ interval, weight, year }) =>
    map.get(interval.index).addValue(year, weight || ZERO)
  );

  return Array.from(map.values());
}

/**
 *
 */
export function fetchCatchHistory(
  options: TCatchFilterSettings,
  period: TCatchesPeriod,
  onSuccess: (history: CatchHistoryRecord[]) => void
): void {
  const { years } = period;
  historyController?.abort();
  if (years.length === 0) return onSuccess([]);

  historyController = new AbortController();

  http
    .send({
      method: 'GET',
      signal: historyController.signal,
      url: ENDPOINTS.VESSEL_TRIP_HISTORY + buildQuery(options, period)
    })
    .then(({ data }: THttpResponse<THistoricalCatchRecord[]>) => {
      onSuccess(mapCatchData(data, years));
    })
    .catch(window.console.error);
}

/**
 *
 */
export function fetchCatchPoints(
  options: TCatchFilterSettings,
  period: TCatchesPeriod,
  zoom: number,
  onSuccess: (history: TVesselTripResponse) => void
): void {
  const { years } = period;
  pointsController?.abort();
  if (years.length === 0) return onSuccess({ activities: [], clusters: [] });

  pointsController = new AbortController();

  http
    .send({
      method: 'GET',
      signal: pointsController.signal,
      url: ENDPOINTS.VESSEL_TRIP + buildQuery(options, period) + `&zoom=${zoom}`
    })
    .then(({ data }: THttpResponse<TVesselTripResponse>) => onSuccess(data))
    .catch(window.console.error);
}

/**
 *
 */
function fetchSpecies(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_SPECIES + query
    })
    .then(({ data }: THttpResponse<TSpecieSimple[]>) =>
      data
        ?.map(
          ({ id, value }): TSelectOption<number> => ({
            label: value.en_GB,
            value: id
          })
        )
        .sort(({ label: a }, { label: b }) => stringComparator(a, b))
    );
}

/**
 *
 */
function fetchTypes(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_TYPES + query
    })
    .then(({ data }: THttpResponse<TIdLocationDict[]>) => {
      const types = (data || []).map(({ id }: TIdLocationDict): number => id);
      return VESSEL_TYPE_LIST.filter(({ value }: TSelectOption<number>) =>
        types.includes(value)
      );
    });
}

/**
 *
 */
function fetchVessels(query: string): Promise<TSelectOption<number>[]> {
  return http
    .send({
      method: 'GET',
      signal: optionsController.signal,
      url: ENDPOINTS.VESSEL_TRIP_VESSELS + query
    })
    .then(({ data }: THttpResponse<TVesselSimple[]>) =>
      data
        .sort(({ name: a }, { name: b }) => stringComparator(a, b))
        .map(
          ({ id, name }): TSelectOption<number> => ({
            label: name,
            value: id
          })
        )
    );
}

/**
 *
 */
export function fetchOptions(
  period: TCatchesPeriod,
  onSuccess: (res: {
    species: TSelectOption<number>[];
    types: TSelectOption<number>[];
    vessels: TSelectOption<number>[];
  }) => void
) {
  const { years } = period;
  if (years.length === 0)
    return onSuccess({
      species: [],
      types: [],
      vessels: []
    });

  const query = buildQuery(null, period);
  optionsController?.abort();
  optionsController = new AbortController();

  Promise.all([fetchSpecies(query), fetchTypes(query), fetchVessels(query)])
    .then(([species, types, vessels]) =>
      onSuccess({
        species: [MultiSelect.getAllOption('All species'), ...species],
        types: [MultiSelect.getAllOption('All types'), ...types],
        vessels: [MultiSelect.getAllOption('All vessels'), ...vessels]
      })
    )
    .catch(window.console.error);
}

/**
 *
 */
function buildQuery(
  options: TCatchFilterSettings,
  period: TCatchesPeriod
): string {
  let query = '';

  Object.entries({ ...options, ...period }).forEach(
    ([key, val]: [string, number | number[]]) => {
      const k = PARAMS_MAP[key];
      if (!k || val === void 0 || (val as number[]).length === 0) return;

      if (Array.isArray(val)) {
        val.forEach((v: number) => (query += `&${k}=${v}`));
      } else {
        query += `&${k}=${val}`;
      }
    }
  );

  return `?${query.slice(1)}`;
}
